<template>
  <div>
    <div class="graph-title">
      <div class="float-left mt-2 left-heading">
        <h2>Pass / Open / Fail</h2>
      </div>
      <div class="float-right right-button">
        <el-select
          v-model="selectedDateRange"
          class="select-primary max-width"
          :class="brand.primary ? 'select-brand' : 'select-primary-orange'"
          placeholder="Filter by Date Range"
          style="width: 150px;"
        >
          <el-option
            v-for="(dateRange, index) in dateRanges"
            :key="index"
            :label="dateRange.label"
            :value="dateRange.value"
          />
        </el-select>
      </div>
      <div class="clearfix"></div>
    </div>

    <highcharts :options="highChartOptions" ref="highcharts"></highcharts>

    <div class="labels-container ignore-translation">
      <div
        class="label-item"
        v-for="(item, index) in labels"
        :key="index"
        @mouseover="showTooltip(index)"
        @mouseout="hideTooltip"
      >
        <i :class="`fa fa-${item.icon}`" :style="{ color: item.iconColor }"></i>
        <span class="label-text">{{ item.name }}</span>
        <span class="label-value">{{ item.count }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { Option, Select } from "element-ui";
import Highcharts from "highcharts";
import HighchartsSolidGauge from "highcharts/modules/solid-gauge";
import HighchartsMore from "highcharts/highcharts-more";
import { Chart } from "highcharts-vue";

import { brandContext } from "@/brand/brandContext";

HighchartsMore(Highcharts);
HighchartsSolidGauge(Highcharts);

export default {
  name: "PassOpenFailGauge",
  props: {
    company_id: String
  },
  components: {
    highcharts: Chart,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  data() {
    return {
      baseColors: {
        pass: "#109618",
        open: "#fedf00",
        fail: "#dc3912",
      },
      highChartOptions: {
        chart: {
          type: "solidgauge",
          height: "70%",
          backgroundColor: "transparent",
        },
        title: {
          text: "",
        },
        tooltip: {
          enabled: true,
          formatter: function () {
            return `<b>${this.point.name}</b>: ${this.point.y.toFixed(1)}%`;
          },
        },
        pane: {
          startAngle: 0,
          endAngle: 360,
          background: [
            {
              outerRadius: "112%",
              innerRadius: "88%",
              backgroundColor: new Highcharts.Color("#109618").setOpacity(0.3).get(),
              borderWidth: 0,
            },
            {
              outerRadius: "87%",
              innerRadius: "63%",
              backgroundColor: new Highcharts.Color("#fedf00").setOpacity(0.3).get(),
              borderWidth: 0,
            },
            {
              outerRadius: "62%",
              innerRadius: "38%",
              backgroundColor: new Highcharts.Color("#dc3912").setOpacity(0.3).get(),
              borderWidth: 0,
            },
          ],
        },
        yAxis: {
          min: 0,
          max: 100,
          lineWidth: 0,
          tickPositions: [],
        },
        plotOptions: {
          solidgauge: {
            dataLabels: {
              enabled: false,
            },
            linecap: "round",
            stickyTracking: false,
            rounded: true,
          },
        },
        series: [
          {
            name: "Pass",
            data: [{ name: "Pass", y: 0, color: "#109618", radius: "112%", innerRadius: "88%" }],
          },
          {
            name: "Open",
            data: [{ name: "Open", y: 0, color: "#fedf00", radius: "87%", innerRadius: "63%" }],
          },
          {
            name: "Fail",
            data: [{ name: "Fail", y: 0, color: "#dc3912", radius: "62%", innerRadius: "38%" }],
          },
        ],
      },
      dateRanges: [
        { label: "Last 30 Days", value: 1 },
        { label: "Last 60 Days", value: 2 },
        { label: "Last 90 Days", value: 3 },
        { label: "6 Months", value: 6 },
        { label: "12 Months", value: 12 },
      ],
      selectedDateRange: 1,
      labels: [
        { name: "Pass", count: 0, percentage: 0, icon: "check", iconColor: "#109618" },
        { name: "Open", count: 0, percentage: 0, icon: "question-circle", iconColor: "#fedf00" },
        { name: "Fail", count: 0, percentage: 0, icon: "times-circle", iconColor: "#dc3912" },
      ],
    };
  },
  mounted() {
    this.fetchData();
  },
  computed: {
    brand() {
      return brandContext.state;
    },
  },
  methods: {
    updateChartData(data) {
      const total = Object.values(data).reduce((sum, value) => sum + value, 0);

      this.labels.forEach((label, index) => {
        const count = data[label.name.toLowerCase()] || 0;
        const percentage = total > 0 ? (count / total) * 100 : 0;

        this.labels[index].count = count;
        this.labels[index].percentage = percentage;
        this.highChartOptions.series[index].data[0].y = percentage; // ✅ Update chart fill based on percentage
      });

      if (this.$refs.highcharts && this.$refs.highcharts.chart) {
        this.$refs.highcharts.chart.update(this.highChartOptions, true, true, true);
      }
    },
    fetchData() {
      this.$http
        .post("analytics/pofe_courses", {
          company_id: this.company_id,
          dateRange: this.selectedDateRange,
        })
        .then((response) => {
          this.updateChartData(response.data);
        })
        .catch((error) => {
          console.error("API failed for loading POF data", error);
        });
    },
    showTooltip(index) {
      if (this.$refs.highcharts && this.$refs.highcharts.chart) {
        const point = this.$refs.highcharts.chart.series[index].points[0];
        this.$refs.highcharts.chart.tooltip.refresh(point);
      }
    },
    hideTooltip() {
      if (this.$refs.highcharts && this.$refs.highcharts.chart) {
        this.$refs.highcharts.chart.tooltip.hide();
      }
    },
  },
  watch: {
    company_id() {
      this.fetchData();
    },
    selectedDateRange() {
      this.fetchData();
    },
  },
};
</script>

<style scoped>
.graph-title {
  margin-bottom: 20px;
}

.left-heading h2 {
  margin: 0;
}

.clearfix::after {
  content: "";
  clear: both;
  display: table;
}

.labels-container {
  display: flex;
  justify-content: center;
  margin-top: 0px;
  margin-bottom: 20px;
}

.label-item {
  display: flex;
  align-items: center;
  margin: 0 15px;
  cursor: pointer;
}

.label-item i {
  font-size: 24px;
  margin-right: 8px;
}

.label-text {
  font-weight: bold;
  margin-right: 4px;
}

.label-value {
  font-size: 16px;
  color: #555;
}
</style>
